import { useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useLocation } from 'react-router-dom';
import * as RadixToast from '@radix-ui/react-toast';

import { ToastItem } from './ToastItem';
import { dismissToastsOffPage, getToasts, subscribeToToasts } from './toastStore';

import styles from './Toast.module.scss';

// A swipe to the right closes a toast once it covers most of the toast's width.
const SWIPE_THRESHOLD_PX = 250;

/** Closes page-scoped toasts when the route leaves their page. */
function ToastPageScope() {
    const { pathname } = useLocation();
    useEffect(() => {
        dismissToastsOffPage(pathname);
    }, [pathname]);
    return null;
}

/**
 * Where toasts appear, raised from anywhere with `ToastInfo` and friends. Radix's viewport is
 * a branch of every dismissable layer, so a press on a toast leaves an open modal open, and
 * each toast is a layer of its own above the modal, so it takes pointer events back from it.
 * The list carries `aria-live` only so that a modal's aria-hidden pass skips it and the
 * toasts stay reachable; Radix announces each toast through its own live region, so the list
 * itself stays silent.
 */
export function Toaster() {
    const { t } = useTranslation();
    const toasts = useSyncExternalStore(subscribeToToasts, getToasts, getToasts);
    const inRouter = useInRouterContext();

    return (
        <RadixToast.Provider
            label={t('molecules.toastTemplate.announceLabel')}
            swipeDirection="right"
            swipeThreshold={SWIPE_THRESHOLD_PX}>
            {inRouter && <ToastPageScope />}
            {toasts.map((entry) => (
                <ToastItem key={entry.key} entry={entry} />
            ))}
            <RadixToast.Viewport
                className={styles.viewport}
                label={t('molecules.toastTemplate.regionLabel', { hotkey: 'F8' })}
                aria-live="off"
            />
        </RadixToast.Provider>
    );
}

export default Toaster;
